interface IntersectionObserverOptions {
	threshold?: number;
	once?: boolean;
	rootMargin?: string;
}

export function intersectionObserver(
	node: HTMLElement,
	{ threshold = 0.1, once = true, rootMargin = '0px' }: IntersectionObserverOptions = {}
) {
	const observer = new IntersectionObserver(
		([entry]) => {
			node.dispatchEvent(
				new CustomEvent('intersect', {
					detail: {
						isIntersecting: entry.isIntersecting,
						entry
					}
				})
			);

			if (once && entry.isIntersecting) {
				observer.unobserve(node);
			}
		},
		{ threshold, rootMargin }
	);

	observer.observe(node);

	return {
		destroy() {
			observer.disconnect();
		}
	};
}

// Enhanced version with viewport percentage support (similar to Framer Motion's useInView)
export function intersectionObserverViewport(
	node: HTMLElement,
	{
		threshold = 0.1,
		once = true,
		amount = 0.3, // Percentage of element visible before triggering
		margin = '-20% 0px'
	}: {
		threshold?: number;
		once?: boolean;
		amount?: number;
		margin?: string;
	} = {}
) {
	// Convert amount to threshold if provided
	const finalThreshold = amount || threshold;

	const observer = new IntersectionObserver(
		([entry]) => {
			const isIntersecting = entry.isIntersecting && entry.intersectionRatio >= finalThreshold;

			node.dispatchEvent(
				new CustomEvent('intersect', {
					detail: {
						isIntersecting,
						entry,
						intersectionRatio: entry.intersectionRatio
					}
				})
			);

			if (once && isIntersecting) {
				observer.unobserve(node);
			}
		},
		{ threshold: finalThreshold, rootMargin: margin }
	);

	observer.observe(node);

	return {
		update(newOptions: { threshold?: number; once?: boolean; amount?: number; margin?: string }) {
			// Handle option updates if needed
			const { threshold: newThreshold = 0.1, amount: newAmount = 0.3 } = newOptions;
			const newFinalThreshold = newAmount || newThreshold;

			if (newFinalThreshold !== finalThreshold) {
				observer.disconnect();
				// Would need to recreate observer with new options
			}
		},
		destroy() {
			observer.disconnect();
		}
	};
}

// Utility for scroll-based animations similar to Framer Motion's whileInView
export function createScrollTrigger(
	callback: (isVisible: boolean, entry?: IntersectionObserverEntry) => void,
	options: IntersectionObserverOptions = {}
) {
	return (node: HTMLElement) => {
		const { threshold = 0.1, once = true, rootMargin = '0px' } = options;

		const observer = new IntersectionObserver(
			([entry]) => {
				callback(entry.isIntersecting, entry);

				if (once && entry.isIntersecting) {
					observer.unobserve(node);
				}
			},
			{ threshold, rootMargin }
		);

		observer.observe(node);

		return {
			destroy() {
				observer.disconnect();
			}
		};
	};
}
